import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Grid } from '@material-ui/core';
import { css } from '@emotion/react';
import { GreyText, TabTitle, TabRow, OnwardToggle, RadioLabelInfo, TextField } from '../../../blocks';
import { UserContext } from '@/components/App';
import { ModalContext } from '..';
import { ORDER_EVENT_LABELS } from '@onward-delivery/core';
import { buildSubscriptionRows, hydrateSubscriptionState } from './utils';

const NotificationsTab = () => {
    const [collapsed, setCollapsed] = useState(false);
    const [selectedEvents, setSelectedEvents] = useState({});
    const [primaryEmail, setPrimaryEmail] = useState('');
    const [secondaryEmails, setSecondaryEmails] = useState('');

    const { user } = useContext(UserContext);
    const userEmail = user?.email;
    const { state: modalState, callbacks } = useContext(ModalContext);

    const eventCategories = {
        Scheduling: [
            'order_scheduled',
            'order_rescheduled',
            'order_unscheduled',
            'order_scheduled_timeframe',
            'order_rescheduled_timeframe',
            'order_pickup_scheduled',
            'order_pickup_rescheduled',
            'order_pickup_scheduled_timeframe',
            'order_pickup_rescheduled_timeframe',
        ],
        'Middle Mile': [
            'mm_pickup_scheduled',
            'mm_delivery_scheduled',
            'mm_arrived',
            'mm_dispatched',
            'mm_in_transit',
            'mm_cancelled',
            'middle_mile_carrier_assigned',
            'middle_mile_return_to_sender',
            'carrier_received',
        ],
        'Order Status': [
            'order_created',
            'order_dispatched',
            'order_in_transit',
            'order_incomplete',
            'order_rejected',
            'order_cancelled',
            'order_completed',
            'carrier_assigned',
            'order_details_updated',
        ],
        'Customer Communications': ['customer_comms_availability', 'customer_comms_predelivery', 'customer_contact'],
        'Documents & Uploads': [
            'pod_uploaded',
            'proof_of_pickup_uploaded',
            'delivery_photo_uploaded',
            'item_labels_uploaded',
        ],
        'Billing & Pricing': [
            'shipper_rate_updated',
            'shipper_invoice_created',
            'carrier_invoice_created',
            'globals:price_order',
        ],
        Miscellaneous: ['exception_reported', 'item_tracking_events'],
    };

    useEffect(() => {
        const existing = modalState?.order?.email_subscriptions;
        if (existing) {
            const { selectedEvents: hydrated, emails } = hydrateSubscriptionState(existing);
            setSelectedEvents(hydrated);
            const [primary = '', ...secondaries] = emails;
            setPrimaryEmail(primary);
            setSecondaryEmails(secondaries.join(', '));
        }
    }, [modalState?.order?.order_id]);

    useEffect(() => {
        const hasSelectedEvents = Object.keys(selectedEvents || {}).length > 0;
        const noEmailsProvided = !primaryEmail && !secondaryEmails?.trim();
        if (hasSelectedEvents && noEmailsProvided && userEmail) {
            setPrimaryEmail(userEmail);
        }
    }, [selectedEvents, userEmail]);

    useEffect(() => {
        callbacks?.modifyOrder?.({
            email_subscriptions: buildSubscriptionRows({ selectedEvents, primaryEmail, secondaryEmails }),
        });
    }, [selectedEvents, primaryEmail, secondaryEmails]);

    return (
        <>
            <TabTitle
                collapsed={collapsed}
                callbacks={{ setCollapsed }}
                css={css`
                    align-items: center;
                    justify-content: space-between;
                `}
            >
                Admin Notifications
            </TabTitle>

            {collapsed ? null : (
                <>
                    <Grid
                        css={css`
                            margin-bottom: 20px;
                        `}
                        item
                    >
                        <GreyText>Select which events you would like to reveive an email for.</GreyText>
                    </Grid>

                    <TabRow>
                        <Grid item xs={12}>
                            <OnwardToggle
                                value={Object.keys(ORDER_EVENT_LABELS).every((key) => !!selectedEvents[key])}
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    if (checked) {
                                        const all = Object.fromEntries(
                                            Object.keys(ORDER_EVENT_LABELS).map((key) => [key, true])
                                        );
                                        setSelectedEvents(all);
                                        if (!primaryEmail && userEmail) {
                                            setPrimaryEmail(userEmail);
                                        }
                                    } else {
                                        setSelectedEvents({});
                                    }
                                }}
                            />
                            <RadioLabelInfo
                                css={css`
                                    color: #000;
                                    padding-top: 3px;
                                    margin-left: 10px;
                                `}
                            >
                                Select All
                            </RadioLabelInfo>
                        </Grid>
                    </TabRow>

                    {Object.entries(eventCategories).map(([category, keys]) => (
                        <React.Fragment key={category}>
                            <TabRow>
                                <Grid item xs={12}>
                                    <GreyText
                                        css={css`
                                            font-weight: bold;
                                            margin: 8px 0 4px 0;
                                        `}
                                    >
                                        {category}
                                    </GreyText>
                                </Grid>
                            </TabRow>
                            <TabRow>
                                {keys
                                    .filter((key) => ORDER_EVENT_LABELS[key])
                                    .map((key) => (
                                        <Grid key={key} item xs={6}>
                                            <OnwardToggle
                                                value={!!selectedEvents[key]}
                                                onChange={(e) => {
                                                    const checked = e.target.checked;
                                                    setSelectedEvents((prev) => {
                                                        const next = { ...prev };
                                                        if (checked) {
                                                            next[key] = true;
                                                            if (!primaryEmail && userEmail) {
                                                                setPrimaryEmail(userEmail);
                                                            }
                                                        } else {
                                                            delete next[key];
                                                        }
                                                        return next;
                                                    });
                                                }}
                                            />
                                            <RadioLabelInfo
                                                css={css`
                                                    color: #000;
                                                    padding-top: 3px;
                                                    margin-left: 10px;
                                                `}
                                            >
                                                {ORDER_EVENT_LABELS[key] || key}
                                            </RadioLabelInfo>
                                        </Grid>
                                    ))}
                            </TabRow>
                        </React.Fragment>
                    ))}
                    <TabRow>
                        <Grid item xs={6}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                label={'Primary Email'}
                                value={primaryEmail}
                                onChange={(e) => setPrimaryEmail(e.target.value)}
                            />
                        </Grid>
                    </TabRow>
                    <TabRow>
                        <Grid item xs={6}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                label={'Additional Emails'}
                                helperText={'Separate multiple emails with commas'}
                                value={secondaryEmails}
                                onChange={(e) => setSecondaryEmails(e.target.value)}
                            />
                        </Grid>
                    </TabRow>
                </>
            )}
        </>
    );
};

export default NotificationsTab;
